<template>
  <slot :number="number" />
</template>

<script lang="ts">
export default defineComponent({
  inheritAttrs: false,
});
</script>

<script setup lang="ts">
import { computed, defineComponent } from 'vue';
import { omit, omitBy, numberFormatter } from '@libs/utils';

type NumberProps = {
  value?: number | string | null;
  locale?: string;
  localeMatcher?: 'lookup' | 'best fit';
  currency?: string;
  currencyDisplay?: 'symbol' | 'code' | 'name';
  numberStyle?: 'decimal' | 'currency' | 'percent' | 'unit';
  useGrouping?: boolean;
  minimumIntegerDigits?: string | number | null;
  minimumFractionDigits?: string | number | null;
  maximumFractionDigits?: string | number | null;
  minimumSignificantDigits?: string | number | null;
  maximumSignificantDigits?: string | number | null;
  notation?: 'standard' | 'scientific' | 'engineering' | 'compact';
  notationLimit?: string | null;
  compactDisplay?: 'short' | 'long';
  signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero';
  round?: boolean | 'up' | 'down';
};

const props = withDefaults(defineProps<NumberProps>(), {
  value: undefined,
  locale: 'en-US',
  localeMatcher: 'best fit',
  currency: 'IRR',
  currencyDisplay: 'symbol',
  numberStyle: 'decimal',
  useGrouping: true,
  minimumIntegerDigits: null,
  minimumFractionDigits: null,
  maximumFractionDigits: null,
  maximumSignificantDigits: null,
  compactDisplay: 'short',
  notation: 'standard',
  notationLimit: null,
  round: false,
  minimumSignificantDigits: null,
  signDisplay: 'auto',
});

const number = computed(() => {
  let propsWithStyle = { ...props, style: props.numberStyle };

  if (props.value && props.notationLimit && props.value < props.notationLimit) {
    propsWithStyle = {
      ...propsWithStyle,
      notation: 'standard',
      minimumFractionDigits: null,
      maximumFractionDigits: null,
    };
  }

  const configs = omitBy(
    omit(propsWithStyle, ['value', 'locale', 'numberStyle']),
    (v) => v === null || v === undefined
  );

  // To prevent compiler error we need to change style prop name
  configs.style = props.numberStyle;

  let roundedValue = Number(props.value);

  if (props.numberStyle === 'percent') {
    roundedValue = roundedValue * 100;
  }

  roundedValue =
    props.round === 'up'
      ? Math.ceil(roundedValue)
      : props.round === 'down'
      ? Math.floor(roundedValue)
      : props.round === true
      ? Math.round(roundedValue)
      : roundedValue;

  if (props.numberStyle === 'percent') {
    roundedValue = roundedValue / 100;
  }

  return numberFormatter(roundedValue, props.locale, configs);
});
</script>
